"use client";
import React, { useState, useEffect, useContext, createContext } from "react";

// Create context
const UserContext = createContext();

// Provider component
export function UserProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState(null);
  const [userPreferences, setUserPreferences] = useState({
    theme: 'nebula',
    notifications: true,
    privacy: 'private'
  });

  // Simulate checking for existing session on mount
  useEffect(() => {
    // For demo purposes, we'll simulate a user session
    // In a real app, this would check localStorage, cookies, or an API
    const simulateAuthCheck = () => {
      const hasSession = localStorage.getItem('demo_user_session');
      
      if (hasSession) {
        try {
          const session = JSON.parse(hasSession);
          setIsAuthenticated(true);
          setUserId(session.userId);
          setUsername(session.username);
          setUserPreferences(session.preferences || userPreferences);
        } catch (error) {
          console.error('Error parsing session:', error);
          // Clear invalid session
          localStorage.removeItem('demo_user_session');
        }
      }
    };
    
    simulateAuthCheck();
  }, []);

  // Login function
  const login = (username, password) => {
    // This is a simulated login for demo purposes
    // In a real app, this would make an API call
    
    return new Promise((resolve, reject) => {
      // Simulate API delay
      setTimeout(() => {
        // Demo credentials check
        if (username === 'demo' && password === 'password') {
          const userData = {
            userId: 'user123',
            username: 'Demo User',
            preferences: {
              theme: 'nebula',
              notifications: true,
              privacy: 'private'
            }
          };
          
          // Set state
          setIsAuthenticated(true);
          setUserId(userData.userId);
          setUsername(userData.username);
          setUserPreferences(userData.preferences);
          
          // Save to localStorage for persistence
          localStorage.setItem('demo_user_session', JSON.stringify(userData));
          
          resolve(userData);
        } else {
          reject(new Error('Invalid credentials'));
        }
      }, 1000);
    });
  };

  // Logout function
  const logout = () => {
    // Clear state
    setIsAuthenticated(false);
    setUserId(null);
    setUsername(null);
    setUserPreferences({
      theme: 'nebula',
      notifications: true,
      privacy: 'private'
    });
    
    // Clear localStorage
    localStorage.removeItem('demo_user_session');
    
    return Promise.resolve();
  };

  // Update user preferences
  const updatePreferences = (newPreferences) => {
    setUserPreferences(prev => {
      const updated = { ...prev, ...newPreferences };
      
      // If authenticated, save to localStorage
      if (isAuthenticated) {
        const session = JSON.parse(localStorage.getItem('demo_user_session') || '{}');
        session.preferences = updated;
        localStorage.setItem('demo_user_session', JSON.stringify(session));
      }
      
      return updated;
    });
  };

  // Context value
  const value = {
    isAuthenticated,
    userId,
    username,
    userPreferences,
    login,
    logout,
    updatePreferences
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

// Custom hook to use the user context
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
